import React, { useState, useEffect } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import TabBar from "../components/ui/TabBar";
import FormField from "../components/ui/FormField";
import DataTable from "../components/ui/DataTable";
import useAuth from "../hooks/useAuth";
import { FiRefreshCw } from "react-icons/fi";
import { useSeason } from "../context/SeasonContext";

const TABS = [
  { id: "list", label: "Farmer List" },
  { id: "fields", label: "Fields List" },
];

export default function Farmers() {
  const [tab, setTab] = useState("list");
  const { refreshToken } = useAuth();
  const { seasons, selectedSeason } = useSeason();
  const currentSeasonObj = seasons?.find((s) => s.season_id.toString() === selectedSeason?.toString());

  const [farmers, setFarmers] = useState([]);
  const [villages, setVillages] = useState([]);
  const [organisers, setOrganisers] = useState([]);

  const [allFields, setAllFields] = useState([]);

  const [editingId, setEditingId] = useState(null);

  const [showFarmerForm, setShowFarmerForm] = useState(false);
  const [showFieldForm, setShowFieldForm] = useState(false);

  const token = sessionStorage.getItem("maaze_token");

  const [form, setForm] = useState({
    village_id: "",
    organiser_id: "",
    farmer_name: "",
    mobile: "",
    aadhaar_no: "",
    bank_account: "",
    ifsc_code: "",
    land_acres: "",
    variety: "",
  });

  const [fieldForm, setFieldForm] = useState({
    farmer_id: "",
    field_name: "",
    area_acres: "",
    survey_no: "",
    soil_type: "",
    irrigation_type: "",
  });

  const getFarmerName = (id) => {
    const farmer = farmers.find((f) => f.farmer_id === id);
    return farmer ? farmer.farmer_name : id;
  };

  /* ================= FETCH FARMERS ================= */

  const fetchFarmers = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/farmers",
      { headers: { Authorization: `Bearer ${token}` } },
    );

    setFarmers(res.data?.data || []);
  };

  /* ================= FETCH ALL FIELDS ================= */

  const fetchAllFields = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/farmers/fields",
      { headers: { Authorization: `Bearer ${token}` } },
    );

    setAllFields(res.data?.data || []);
  };

  /* ================= FETCH VILLAGES ================= */

  const fetchVillages = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/villages",
      { headers: { Authorization: `Bearer ${token}` } },
    );

    setVillages(res.data?.data || []);
  };

  /* ================= FETCH ORGANISERS ================= */

  const fetchOrganisers = async () => {
    const res = await axios.get(
      "https://maize.norissolutions.in/Php/public/api/organisers",
      { headers: { Authorization: `Bearer ${token}` } },
    );

    setOrganisers(res.data?.data || []);
  };

  useEffect(() => {
    fetchFarmers();
    fetchVillages();
    fetchOrganisers();
  }, []);

  useEffect(() => {
    if (tab === "fields") fetchAllFields();
  }, [tab]);

  /* ================= SAVE FARMER ================= */

  const handleSubmit = async () => {
    const payload = {
      ...form,
      village_id: Number(form.village_id),
      organiser_id: Number(form.organiser_id),
      land_acres: Number(form.land_acres),
    };

    if (editingId) {
      await axios.put(
        `https://maize.norissolutions.in/Php/public/api/farmers/${editingId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } else {
      await axios.post(
        "https://maize.norissolutions.in/Php/public/api/farmers",
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    }

    fetchFarmers();
    setShowFarmerForm(false);
    resetForm();
  };

  /* ================= DELETE FARMER ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this farmer?")) return;

    await axios.delete(
      `https://maize.norissolutions.in/Php/public/api/farmers/${id}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    fetchFarmers();
  };

  /* ================= EDIT FARMER ================= */

  const handleEdit = (row) => {
    setForm({
      village_id: row.village_id,
      organiser_id: row.organiser_id,
      farmer_name: row.farmer_name,
      mobile: row.mobile,
      aadhaar_no: row.aadhaar_no,
      bank_account: row.bank_account,
      ifsc_code: row.ifsc_code,
      land_acres: row.land_acres,
      variety: row.variety || "",
    });

    setEditingId(row.farmer_id);
    setShowFarmerForm(true);
  };

  /* ================= ADD FIELD ================= */

  const handleAddField = async () => {
    const payload = {
      field_name: fieldForm.field_name,
      area_acres: Number(fieldForm.area_acres),
      survey_no: fieldForm.survey_no,
      soil_type: fieldForm.soil_type,
      irrigation_type: fieldForm.irrigation_type,
    };

    await axios.post(
      `https://maize.norissolutions.in/Php/public/api/farmers/${fieldForm.farmer_id}/fields`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    fetchAllFields();
    setShowFieldForm(false);

    setFieldForm({
      farmer_id: "",
      field_name: "",
      area_acres: "",
      survey_no: "",
      soil_type: "",
      irrigation_type: "",
    });
  };

  /* ================= RESET ================= */

  const resetForm = () => {
    setEditingId(null);

    setForm({
      village_id: "",
      organiser_id: "",
      farmer_name: "",
      mobile: "",
      aadhaar_no: "",
      bank_account: "",
      ifsc_code: "",
      land_acres: "",
      variety: "",
    });
  };

  /* ================= TABLES ================= */

  const COLUMNS = [
    { key: "farmer_id", label: "ID" },
    { key: "farmer_name", label: "Farmer" },
    { key: "village_name", label: "Village" },
    { key: "organiser_name", label: "Organiser" },
    { key: "mobile", label: "Mobile" },
    { key: "land_acres", label: "Acres" },
    { key: "variety", label: "Variety" },
    { 
      key: "sowing_date", 
      label: "Sowing Date",
      render: () => currentSeasonObj?.start_date || "-"
    },
    { 
      key: "irrigation_date", 
      label: "Irrigation Date",
      render: () => currentSeasonObj?.end_date || "-"
    },
    {
      key: "_actions",
      label: "Actions",
      render: (_, row) => (
        <>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
            ✏️
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.farmer_id)}
          >
            🗑
          </Button>
        </>
      ),
    },
  ];
  const FIELD_COLUMNS = [
    { key: "field_id", label: "ID" },

    {
      key: "farmer_id",
      label: "Farmer",
      render: (_, row) => getFarmerName(row.farmer_id),
    },

    { key: "field_name", label: "Field Name" },
    { key: "area_acres", label: "Area" },
    { key: "survey_no", label: "Survey No" },
    { key: "soil_type", label: "Soil" },
    { key: "irrigation_type", label: "Irrigation" },
  ];

  return (
    <div>
      <PageHeader title="Farmers">
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === "list" && (
        <Card
          title="All Farmers"
          action={
            <Button size="sm" onClick={() => setShowFarmerForm(true)}>
              + Add Farmer
            </Button>
          }
          noPad
        >
          <DataTable columns={COLUMNS} rows={farmers} />
        </Card>
      )}

      {tab === "fields" && (
        <Card
          title="All Fields"
          action={
            <Button size="sm" onClick={() => setShowFieldForm(true)}>
              + Add Field
            </Button>
          }
          noPad
        >
          <DataTable columns={FIELD_COLUMNS} rows={allFields} />
        </Card>
      )}

      {/* ================= FARMER MODAL ================= */}

      {showFarmerForm && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h2>{editingId ? "Edit Farmer" : "Add Farmer"}</h2>

            <div className="form-grid">
              <FormField
                label="Farmer Name"
                value={form.farmer_name}
                onChange={(e) =>
                  setForm({ ...form, farmer_name: e.target.value })
                }
              />

              <FormField
                label="Mobile"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />

              <FormField
                label="Aadhaar No"
                value={form.aadhaar_no}
                onChange={(e) =>
                  setForm({ ...form, aadhaar_no: e.target.value })
                }
              />

              <FormField
                label="Village"
                type="select"
                options={villages.map((v) => ({
                  label: v.village_name,
                  value: v.village_id,
                }))}
                value={form.village_id}
                onChange={(e) => {
                  const selected = villages.find(
                    (v) => v.village_id === Number(e.target.value),
                  );
                  setForm({
                    ...form,
                    village_id: selected?.village_id || "",
                    organiser_id: selected?.organiser_id || "",
                  });
                }}
              />

              <FormField
                label="Organiser"
                type="select"
                options={organisers.map((o) => ({
                  label: o.organiser_name,
                  value: o.organiser_id,
                }))}
                value={form.organiser_id}
                disabled
              />

              <FormField
                label="Bank Account"
                value={form.bank_account}
                onChange={(e) =>
                  setForm({ ...form, bank_account: e.target.value })
                }
              />

              <FormField
                label="IFSC Code"
                value={form.ifsc_code}
                onChange={(e) =>
                  setForm({ ...form, ifsc_code: e.target.value })
                }
              />

              <FormField
                label="Land (Acres)"
                value={form.land_acres}
                onChange={(e) =>
                  setForm({ ...form, land_acres: e.target.value })
                }
              />

              <FormField
                label="Variety"
                value={form.variety}
                onChange={(e) =>
                  setForm({ ...form, variety: e.target.value })
                }
              />
            </div>

            <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
              <Button onClick={handleSubmit}>Save</Button>
              <Button
                variant="outline"
                onClick={() => setShowFarmerForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ================= FIELD MODAL ================= */}

      {showFieldForm && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h2>Add Field</h2>

            <div className="form-grid">
              <FormField
                label="Farmer"
                type="select"
                options={farmers.map((f) => ({
                  label: f.farmer_name,
                  value: f.farmer_id,
                }))}
                value={fieldForm.farmer_id}
                onChange={(e) => {
                  const selectedFarmer = farmers.find(
                    (f) => f.farmer_id === Number(e.target.value),
                  );

                  setFieldForm({
                    ...fieldForm,
                    farmer_id: e.target.value,
                    area_acres: selectedFarmer?.land_acres || "",
                  });
                }}
              />

              <FormField
                label="Field Name"
                value={fieldForm.field_name}
                onChange={(e) =>
                  setFieldForm({ ...fieldForm, field_name: e.target.value })
                }
              />

              <FormField
                label="Area (Acres)"
                value={fieldForm.area_acres}
                onChange={(e) =>
                  setFieldForm({ ...fieldForm, area_acres: e.target.value })
                }
              />

              <FormField
                label="Survey No"
                value={fieldForm.survey_no}
                onChange={(e) =>
                  setFieldForm({ ...fieldForm, survey_no: e.target.value })
                }
              />

              <FormField
                label="Soil Type"
                value={fieldForm.soil_type}
                onChange={(e) =>
                  setFieldForm({ ...fieldForm, soil_type: e.target.value })
                }
              />

              <FormField
                label="Irrigation Type"
                value={fieldForm.irrigation_type}
                onChange={(e) =>
                  setFieldForm({
                    ...fieldForm,
                    irrigation_type: e.target.value,
                  })
                }
              />
            </div>

            <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
              <Button onClick={handleAddField}>Save Field</Button>
              <Button variant="outline" onClick={() => setShowFieldForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
